import {
    type CreateTaskListInput,
    type Task,
    type TaskGroupWithTaskOccurrences,
    type TaskListWithTaskOccurrences,
    type TaskWithOccurrences,
    type UpdateTaskListWithDetailsInput,
} from "@dashboard/api/task";
import type { GroupFormValues, ListFormValues, TaskFormValues } from "../utils";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import {
    taskToCopyInput,
    useCreateList,
    usePatchList,
    useUpdateList,
} from "@dashboard/hooks/useListQuery";
import { createFormHook, formOptions } from "@tanstack/react-form";
import { useMemo } from "react";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { VALIDATION_LIMITS } from "../../AnnualCycle/TaskForm/formHooks";
import { fieldContext, formContext } from "./Form";
import { Checkbox, Input, SubmitButton, TextArea, UserSelect } from "./FormComponents";

export const VALIDATION_LIMITS_LISTS = {
    LIST_TITLE_MAX: 100,
    LIST_DESCRIPTION_MAX: 500,
    GROUP_TITLE_MAX: 100,
} as const;

const { useAppForm: useListForm, withForm } = createFormHook({
    fieldContext,
    formContext,
    fieldComponents: { Checkbox, Input, TextArea, UserSelect },
    formComponents: { SubmitButton },
});

const mapTaskToForm = (task: Task): TaskFormValues => ({
    clientId: `task-${task.id}`,
    id: task.id,
    title: task.title ?? "",
    description: task.description ?? null,
    groupId: task.groupId,
});

const mapGroupToForm = (group: TaskGroupWithTaskOccurrences): GroupFormValues => ({
    id: group.id,
    title: group.title,
    tasks: group.tasks.map(mapTaskToForm),
});

const mapListToForm = (list: TaskListWithTaskOccurrences): ListFormValues => ({
    title: list.title,
    description: list.description,
    template: list.template,
    responsibleUserId: list.responsibleUserId ?? null,
    tasks: list.tasks?.map(mapTaskToForm) ?? [],
    groups: list.groups?.map(mapGroupToForm) ?? [],
});

const mapTaskToCopyForm = (task: TaskWithOccurrences): TaskFormValues => ({
    clientId: `copy-${task.id}`,
    title: task.title ?? "",
    description: task.description ?? null,
    // Same fields as "Copy", except status: a checklist made from a template starts fresh
    copy: { ...taskToCopyInput(task), status: undefined },
});

// A copy starts as a new regular list: no ids, so nothing points back at the source.
const mapListToCopyForm = (list: TaskListWithTaskOccurrences): ListFormValues => ({
    title: list.title,
    description: list.description,
    template: false,
    responsibleUserId: list.responsibleUserId ?? null,
    tasks: list.tasks?.map(mapTaskToCopyForm) ?? [],
    groups:
        list.groups?.map((group) => ({
            title: group.title,
            tasks: group.tasks.map(mapTaskToCopyForm),
        })) ?? [],
});

const mapFormToContent = ({ tasks, groups }: ListFormValues) => ({
    tasks: tasks.map((task) => ({
        id: task.id,
        title: task.title,
        description: task.description ?? null,
    })),
    groups: groups.map((group) => ({
        id: group.id,
        title: group.title,
        tasks: group.tasks.map((task) => ({
            id: task.id,
            title: task.title,
            description: task.description ?? null,
        })),
    })),
});

const listFormDefaultValues: ListFormValues = {
    title: "",
    description: "",
    template: false,
    responsibleUserId: null,
    tasks: [],
    groups: [],
};

export const useListFormSetup = () => {
    const { t } = useTranslation();
    const { instanceId } = useHseInstance();
    const createMutation = useCreateList();
    const updateMutation = useUpdateList();
    const patchMutation = usePatchList();

    const listFormOptions = useMemo(
        () =>
            formOptions({
                defaultValues: listFormDefaultValues,
                validators: {
                    onSubmit: z.object({
                        title: z
                            .string()
                            .min(1, t("common.titleIsRequired"))
                            .max(
                                VALIDATION_LIMITS_LISTS.LIST_TITLE_MAX,
                                t("common.titleMaxLength"),
                            ),

                        description: z
                            .string()
                            .max(
                                VALIDATION_LIMITS_LISTS.LIST_DESCRIPTION_MAX,
                                t("common.descriptionMaxLength"),
                            )
                            .nullable(),

                        template: z.boolean(),

                        responsibleUserId: z.number().positive().nullable(),

                        tasks: z.array(
                            z.object({
                                clientId: z.string().optional(),
                                id: z.number().optional(),
                                title: z
                                    .string()
                                    .min(1, t("common.titleIsRequired"))
                                    .max(
                                        VALIDATION_LIMITS.TITLE_MAX_LENGTH,
                                        t("common.titleMaxLength"),
                                    ),
                                description: z
                                    .string()
                                    .max(
                                        VALIDATION_LIMITS.DESCRIPTION_MAX_LENGTH,
                                        t("common.descriptionMaxLength"),
                                    )
                                    .nullable()
                                    .optional(),
                                groupId: z.number().nullable().optional(),
                            }),
                        ),

                        groups: z.array(
                            z.object({
                                id: z.number().optional(),
                                title: z
                                    .string()
                                    .min(1, t("common.titleIsRequired"))
                                    .max(
                                        VALIDATION_LIMITS_LISTS.GROUP_TITLE_MAX,
                                        t("common.titleMaxLength"),
                                    ),
                                tasks: z.array(
                                    z.object({
                                        clientId: z.string().optional(),
                                        id: z.number().optional(),
                                        title: z
                                            .string()
                                            .min(1, t("common.titleIsRequired"))
                                            .max(
                                                VALIDATION_LIMITS.TITLE_MAX_LENGTH,
                                                t("common.titleMaxLength"),
                                            ),
                                        description: z
                                            .string()
                                            .max(
                                                VALIDATION_LIMITS.DESCRIPTION_MAX_LENGTH,
                                                t("common.descriptionMaxLength"),
                                            )
                                            .nullable()
                                            .optional(),
                                        groupId: z.number().nullable().optional(),
                                    }),
                                ),
                            }),
                        ),
                    }),
                },
            }),
        [t],
    );

    const handleListSubmit = async (
        formValues: ListFormValues,
        listId?: number,
        originalList?: TaskListWithTaskOccurrences,
    ) => {
        try {
            if (listId && originalList) {
                const listData = {
                    title: formValues.title,
                    description: formValues.description,
                    template: formValues.template,
                    responsibleUserId: formValues.responsibleUserId,
                };
                const content = mapFormToContent(formValues);
                const isContentChanged =
                    JSON.stringify(content) !==
                    JSON.stringify(mapFormToContent(mapListToForm(originalList)));

                // Saving tasks rewrites every task, so list-only changes go through PATCH
                if (isContentChanged) {
                    const updateData: UpdateTaskListWithDetailsInput = { ...listData, ...content };
                    await updateMutation.mutateAsync({ id: listId, data: updateData });
                } else {
                    await patchMutation.mutateAsync({ id: listId, data: listData });
                }
            } else {
                const createData: CreateTaskListInput = {
                    instanceId,
                    title: formValues.title,
                    description: formValues.description,
                    template: formValues.template,
                    responsibleUserId: formValues.responsibleUserId ?? undefined,
                    tasks: formValues.tasks.map((task) => ({
                        ...task.copy,
                        title: task.title,
                        description: task.description ?? null,
                    })),
                    groups: formValues.groups.map((group) => ({
                        title: group.title,
                        tasks: group.tasks.map((task) => ({
                            ...task.copy,
                            title: task.title,
                            description: task.description ?? null,
                        })),
                    })),
                };
                await createMutation.mutateAsync(createData);
            }
            return { success: true };
        } catch (error) {
            console.error("Error submitting list form:", error);
            return { success: false, error };
        }
    };

    return {
        useListForm,
        withForm,
        formOpts: listFormOptions,
        mapListToForm,
        mapListToCopyForm,
        handleListSubmit,
    };
};
